import { useState } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast.success('Message Sent Successfully!', {
        description: response.data.message,
        duration: 5000,
      });
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Error Sending Message', {
        description: error.response?.data?.detail || 'Failed to send message. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6 text-blue-700" />,
      title: 'Location',
      content: 'Singapore',
      subContent: 'Gateway to Asia'
    },
    {
      icon: <Mail className="w-6 h-6 text-blue-700" />,
      title: 'Email',
      content: 'info@aquavera.net',
      link: 'mailto:info@aquavera.net'
    },
    {
      icon: <Phone className="w-6 h-6 text-blue-700" />,
      title: 'Phone',
      content: '+65 XXXX XXXX',
      subContent: 'Mon-Fri, 9AM-6PM SGT'
    }
  ];

  return (
    <div className="flex-1">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Let's Start a Conversation
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Ready to explore how Aquavera can support your cross-border growth? Get in touch with our team.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Send us a Message</h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <Label htmlFor="company" className="text-slate-700 font-medium mb-2 block">
                    Company Name *
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Ltd."
                    className="w-full"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-medium mb-2 block">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="w-full"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-slate-700 font-medium mb-2 block">
                    Service Interest *
                  </Label>
                  <Select onValueChange={handleServiceChange} value={formData.service} required>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="market-entry">Market Entry Strategy</SelectItem>
                      <SelectItem value="partnerships">Strategic Partnerships</SelectItem>
                      <SelectItem value="business-dev">Business Development Support</SelectItem>
                      <SelectItem value="capital">Capital Introduction</SelectItem>
                      <SelectItem value="other">Other / General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 font-medium mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your business and expansion goals..."
                    className="w-full min-h-[150px] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white font-medium"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Contact Information</h2>
              <p className="text-slate-600 mb-8">
                Reach out to us through any of these channels.
              </p>
              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-start">
                        <div className="mr-4 mt-1">{info.icon}</div>
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                          {info.link ? (
                            <a
                              href={info.link}
                              className="text-slate-700 hover:text-blue-700 transition-colors"
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-slate-700">{info.content}</p>
                          )}
                          {info.subContent && (
                            <p className="text-sm text-slate-500 mt-1">{info.subContent}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Additional Info */}
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Book a Consultation</h3>
                <p className="text-slate-600 mb-6">
                  Schedule a complimentary 30-minute consultation to discuss your expansion goals and explore how we can help.
                </p>
                <Button className="w-full bg-blue-700 hover:bg-blue-800 text-white font-medium">
                  Schedule Meeting
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map or Additional CTA */}
      <section className="py-24 bg-blue-700 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Based in Singapore, Serving the World
          </h2>
          <p className="text-xl text-blue-100">
            Our Singapore headquarters positions us at the heart of Asia's economic dynamism,
            enabling us to provide on-the-ground insights and facilitate strategic connections globally.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
